/**
 * Core API - Data Sources & Field Mappings
 *
 * Database operations for data sources (InfluxDB, PostgreSQL, etc.),
 * datapoints, and field mappings (linking datapoints to factory structure).
 * Uses core schema for multi-tenant isolation.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

// =============================================================================
// Types
// =============================================================================

export type DataSourceType = 'influxdb_v2' | 'postgresql' | 'mysql';

export interface DataSource {
  id: string;
  tenant_id: string;
  name: string;
  source_type: DataSourceType;
  config: Json;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface InfluxDBConfig {
  url: string;
  org: string;
  token: string;
  bucket?: string;
}

export interface PostgreSQLConfig {
  host: string;
  port: number;
  database: string;
  user: string;
  password: string;
  ssl?: boolean;
}

export interface MySQLConfig {
  host: string;
  port: number;
  database: string;
  user: string;
  password: string;
  ssl?: boolean;
}

export type DataSourceConfig = InfluxDBConfig | PostgreSQLConfig | MySQLConfig;

export interface Datapoint {
  id: string;
  tenant_id: string;
  data_source_id: string;
  bucket_name: string;
  measurement_name: string;
  field_name: string;
  display_name: string;
  description: string | null;
  datatype: string | null;
  unit_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Field mapping links a datapoint to factory structure (area, line, station).
 * In core schema, this uses datapoint_id rather than embedding all datapoint fields.
 */
export interface FieldMapping {
  id: string;
  tenant_id: string;
  factory_id: string;
  datapoint_id: string;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FieldMappingWithDatapoint extends FieldMapping {
  datapoint?: Datapoint | null;
}

export interface CreateDataSourceInput {
  tenant_id: string;
  name: string;
  source_type: DataSourceType;
  config: InfluxDBConfig | PostgreSQLConfig | MySQLConfig | Json;
  is_active?: boolean;
}

export interface CreateDatapointInput {
  tenant_id: string;
  data_source_id: string;
  bucket_name: string;
  measurement_name: string;
  field_name: string;
  display_name: string;
  description?: string | null;
  datatype?: string | null;
  unit_id?: string | null;
  is_active?: boolean;
}

export interface CreateFieldMappingInput {
  tenant_id: string;
  factory_id: string;
  datapoint_id: string;
  area_id?: string | null;
  line_id?: string | null;
  station_id?: string | null;
  is_active?: boolean;
}

// =============================================================================
// Type Guards & Validation
// =============================================================================

export function isInfluxDBConfig(config: unknown): config is InfluxDBConfig {
  if (!config || typeof config !== 'object') return false;
  const c = config as Record<string, unknown>;
  return 'url' in c && 'org' in c && 'token' in c;
}

export function isPostgreSQLConfig(config: unknown): config is PostgreSQLConfig {
  if (!config || typeof config !== 'object') return false;
  const c = config as Record<string, unknown>;
  return 'host' in c && 'database' in c && 'user' in c && !('url' in c);
}

export function isMySQLConfig(config: unknown): config is MySQLConfig {
  if (!config || typeof config !== 'object') return false;
  const c = config as Record<string, unknown>;
  return 'host' in c && 'database' in c && 'user' in c && !('url' in c);
}

export function validateInfluxDBConfig(config: Partial<InfluxDBConfig>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (!config.url?.trim()) errors.push('URL is required');
  if (!config.org?.trim()) errors.push('Organization is required');
  if (!config.token?.trim()) errors.push('API Token is required');
  
  if (config.url && !config.url.startsWith('http://') && !config.url.startsWith('https://')) {
    errors.push('URL must start with http:// or https://');
  }
  
  return { valid: errors.length === 0, errors };
}

export function validatePostgreSQLConfig(config: Partial<PostgreSQLConfig>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (!config.host?.trim()) errors.push('Host is required');
  if (!config.database?.trim()) errors.push('Database is required');
  if (!config.user?.trim()) errors.push('Username is required');
  if (config.port && (config.port < 1 || config.port > 65535)) {
    errors.push('Port must be between 1 and 65535');
  }
  return { valid: errors.length === 0, errors };
}

export function validateMySQLConfig(config: Partial<MySQLConfig>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (!config.host?.trim()) errors.push('Host is required');
  if (!config.database?.trim()) errors.push('Database is required');
  if (!config.user?.trim()) errors.push('Username is required');
  if (config.port && (config.port < 1 || config.port > 65535)) {
    errors.push('Port must be between 1 and 65535');
  }
  return { valid: errors.length === 0, errors };
}

export function validateDataSourceConfig(
  sourceType: DataSourceType,
  config: Partial<DataSourceConfig>
): { valid: boolean; errors: string[] } {
  switch (sourceType) {
    case 'influxdb_v2':
      return validateInfluxDBConfig(config as Partial<InfluxDBConfig>);
    case 'postgresql':
      return validatePostgreSQLConfig(config as Partial<PostgreSQLConfig>);
    case 'mysql':
      return validateMySQLConfig(config as Partial<MySQLConfig>);
    default:
      return { valid: false, errors: ['Unknown source type'] };
  }
}

// =============================================================================
// Display Helpers
// =============================================================================

export const DATA_SOURCE_TYPES = [
  { 
    id: "influxdb_v2" as const, 
    name: "InfluxDB v2", 
    icon: "Database",
    color: "text-chart-3", 
    bgColor: "bg-chart-3/10",
    description: "Time-series database for metrics and IoT data"
  },
  { 
    id: "postgresql" as const, 
    name: "PostgreSQL", 
    icon: "Table2",
    color: "text-chart-2", 
    bgColor: "bg-chart-2/10",
    description: "Relational database with SQL support"
  },
  { 
    id: "mysql" as const, 
    name: "MySQL", 
    icon: "Database",
    color: "text-chart-1", 
    bgColor: "bg-chart-1/10",
    description: "Popular open-source relational database"
  },
] as const;

export function getDataSourceTypeInfo(type: DataSourceType) {
  return DATA_SOURCE_TYPES.find(t => t.id === type) ?? DATA_SOURCE_TYPES[0];
}

export function getConfigDisplayUrl(config: Json, sourceType: DataSourceType): string {
  if (!config || typeof config !== 'object') return 'No connection info';
  
  if (sourceType === 'influxdb_v2' && isInfluxDBConfig(config)) {
    return config.url;
  }
  if ((sourceType === 'postgresql' || sourceType === 'mysql') && isPostgreSQLConfig(config)) {
    const port = config.port || (sourceType === 'mysql' ? 3306 : 5432);
    return `${config.host}:${port}/${config.database}`;
  }
  return 'No connection info';
}

// =============================================================================
// Data Source Queries
// =============================================================================

/**
 * Fetches all data sources for a tenant.
 */
export async function fetchDataSourcesByTenant(tenantId: string): Promise<DataSource[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('data_sources')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('name');

  if (error) throw error;
  return data as unknown as DataSource[];
}

/**
 * Fetches all data sources for a factory.
 */
export async function fetchDataSourcesByFactory(factoryId: string): Promise<DataSource[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('data_sources')
    .select('*')
    .eq('factory_id', factoryId)
    .order('name');

  if (error) throw error;
  return data as unknown as DataSource[];
}

/**
 * Fetches active data sources for a factory.
 */
export async function fetchActiveDataSources(factoryId: string): Promise<DataSource[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('data_sources')
    .select('*')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('name');

  if (error) throw error;
  return data as unknown as DataSource[];
}

/**
 * Fetches a single data source by ID.
 */
export async function fetchDataSourceById(dataSourceId: string): Promise<DataSource | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('data_sources')
    .select('*')
    .eq('id', dataSourceId)
    .maybeSingle();

  if (error) throw error;
  return data as unknown as DataSource | null;
}

/**
 * Creates a new data source.
 * Note: factory_id is still required by schema but we're moving toward tenant-level sources.
 */
export async function createDataSource(input: CreateDataSourceInput & { factory_id: string }): Promise<DataSource> {
  const { data, error } = await supabase
    .schema('core')
    .from('data_sources')
    .insert({
      tenant_id: input.tenant_id,
      factory_id: input.factory_id,
      name: input.name,
      source_type: input.source_type,
      config: input.config as Json,
      is_active: input.is_active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return data as unknown as DataSource;
}

/**
 * Updates a data source.
 */
export async function updateDataSource(
  dataSourceId: string,
  updates: Partial<Pick<DataSource, 'name' | 'config' | 'is_active'>>
): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('data_sources')
    .update(updates)
    .eq('id', dataSourceId);

  if (error) throw error;
}

/**
 * Toggles a data source active status.
 */
export async function toggleDataSourceActive(dataSourceId: string, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('data_sources')
    .update({ is_active: isActive })
    .eq('id', dataSourceId);

  if (error) throw error;
}

/**
 * Deletes a data source.
 */
export async function deleteDataSource(dataSourceId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('data_sources')
    .delete()
    .eq('id', dataSourceId);

  if (error) throw error;
}

// =============================================================================
// Datapoint Queries
// =============================================================================

/**
 * Fetches all datapoints for a data source.
 */
export async function fetchDatapointsByDataSource(dataSourceId: string): Promise<Datapoint[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('datapoints')
    .select('*')
    .eq('data_source_id', dataSourceId)
    .order('display_name');

  if (error) throw error;
  return data;
}

/**
 * Fetches active datapoints for a data source.
 */
export async function fetchActiveDatapoints(dataSourceId: string): Promise<Datapoint[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('datapoints')
    .select('*')
    .eq('data_source_id', dataSourceId)
    .eq('is_active', true)
    .order('display_name');

  if (error) throw error;
  return data;
}

/**
 * Creates a new datapoint.
 */
export async function createDatapoint(input: CreateDatapointInput): Promise<Datapoint> {
  const { data, error } = await supabase
    .schema('core')
    .from('datapoints')
    .insert({
      tenant_id: input.tenant_id,
      data_source_id: input.data_source_id,
      bucket_name: input.bucket_name,
      measurement_name: input.measurement_name,
      field_name: input.field_name,
      display_name: input.display_name,
      description: input.description || null,
      datatype: input.datatype || null,
      unit_id: input.unit_id || null,
      is_active: input.is_active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// =============================================================================
// Field Mapping Queries
// =============================================================================

/**
 * Fetches all field mappings for a factory with datapoint details.
 */
export async function fetchFieldMappingsByFactory(factoryId: string): Promise<FieldMappingWithDatapoint[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('field_mappings')
    .select(`
      *,
      datapoint:datapoints(*)
    `)
    .eq('factory_id', factoryId);

  if (error) throw error;
  return data as unknown as FieldMappingWithDatapoint[];
}

/**
 * Fetches field mappings for a specific station.
 */
export async function fetchFieldMappingsByStation(stationId: string): Promise<FieldMappingWithDatapoint[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('field_mappings')
    .select(`
      *,
      datapoint:datapoints(*)
    `)
    .eq('station_id', stationId)
    .eq('is_active', true);

  if (error) throw error;
  return data as unknown as FieldMappingWithDatapoint[];
}

/**
 * Fetches field mappings for an area.
 */
export async function fetchFieldMappingsByArea(areaId: string): Promise<FieldMappingWithDatapoint[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('field_mappings')
    .select(`
      *,
      datapoint:datapoints(*)
    `)
    .eq('area_id', areaId)
    .eq('is_active', true);

  if (error) throw error;
  return data as unknown as FieldMappingWithDatapoint[];
}

/**
 * Fetches field mappings for a line.
 */
export async function fetchFieldMappingsByLine(lineId: string): Promise<FieldMappingWithDatapoint[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('field_mappings')
    .select(`
      *,
      datapoint:datapoints(*)
    `)
    .eq('line_id', lineId)
    .eq('is_active', true);

  if (error) throw error;
  return data as unknown as FieldMappingWithDatapoint[];
}

/**
 * Creates a new field mapping.
 */
export async function createFieldMapping(input: CreateFieldMappingInput): Promise<FieldMapping> {
  const { data, error } = await supabase
    .schema('core')
    .from('field_mappings')
    .insert({
      tenant_id: input.tenant_id,
      factory_id: input.factory_id,
      datapoint_id: input.datapoint_id,
      area_id: input.area_id || null,
      line_id: input.line_id || null,
      station_id: input.station_id || null,
      is_active: input.is_active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Deletes a field mapping.
 */
export async function deleteFieldMapping(fieldMappingId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('field_mappings')
    .delete()
    .eq('id', fieldMappingId);

  if (error) throw error;
}

// =============================================================================
// Connection Testing
// =============================================================================

export interface TestConnectionResult {
  success: boolean;
  message: string;
  latencyMs?: number;
}

/**
 * Converts technical error messages to user-friendly messages.
 */
function formatConnectionError(error: unknown, sourceType: DataSourceType): string {
  const message = error instanceof Error ? error.message : String(error);
  
  // Edge Function returned a non-2xx status code - typically means the function 
  // returned an error response (like 404 for "Data source not found")
  if (message.includes('non-2xx status code') || message.includes('FunctionsHttpError')) {
    return 'Connection test failed. Please check your configuration and try again.';
  }
  
  // Network errors
  if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
    return 'Network error. Please check your internet connection.';
  }
  
  // Timeout errors
  if (message.includes('timeout') || message.includes('Timeout')) {
    return 'Connection timed out. The server may be unreachable.';
  }
  
  // Authentication errors
  if (message.includes('Unauthorized') || message.includes('401')) {
    return 'Authentication failed. Please check your credentials.';
  }
  
  // Specific database errors
  if (sourceType === 'influxdb_v2') {
    if (message.includes('bucket')) {
      return 'Could not access InfluxDB buckets. Check URL, organization, and API token.';
    }
  }
  
  // If it's a reasonably short message, show it
  if (message.length < 100 && !message.includes('Error:')) {
    return message;
  }
  
  // Fallback for unknown errors
  return 'Connection failed. Please verify your settings.';
}

/**
 * Tests connection to a data source.
 */
export async function testDataSourceConnection(dataSourceId: string): Promise<TestConnectionResult> {
  const startTime = Date.now();
  
  try {
    const dataSource = await fetchDataSourceById(dataSourceId);
    if (!dataSource) {
      return { success: false, message: 'Data source not found' };
    }

    let functionName: string;
    let action: string;

    switch (dataSource.source_type) {
      case 'influxdb_v2':
        functionName = 'influxdb-browser';
        action = 'buckets';
        break;
      case 'postgresql':
        functionName = 'postgresql-browser';
        action = 'schemas';
        break;
      case 'mysql':
        functionName = 'mysql-browser';
        action = 'schemas';
        break;
      default:
        return { success: false, message: 'Unknown source type' };
    }

    const { data, error } = await supabase.functions.invoke(functionName, {
      body: { action, dataSourceId },
    });

    const latencyMs = Date.now() - startTime;

    if (error) {
      return { 
        success: false, 
        message: formatConnectionError(error, dataSource.source_type), 
        latencyMs 
      };
    }

    if (data?.error) {
      // The edge function returned an error in the response body
      return { 
        success: false, 
        message: formatConnectionError(data.error, dataSource.source_type), 
        latencyMs 
      };
    }

    return { 
      success: true, 
      message: `Connected (${latencyMs}ms)`,
      latencyMs 
    };
  } catch (err) {
    const latencyMs = Date.now() - startTime;
    
    // Try to fetch the data source type for better error formatting
    let sourceType: DataSourceType = 'influxdb_v2';
    try {
      const ds = await fetchDataSourceById(dataSourceId);
      if (ds) sourceType = ds.source_type;
    } catch {
      // Ignore - use default
    }
    
    return { 
      success: false, 
      message: formatConnectionError(err, sourceType),
      latencyMs 
    };
  }
}
